import React from 'react';
import type { DataRow, ColumnDefinition } from '../types';
import { renderCell } from '../lib/renderCell';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StackedRowsProps {
  rows: DataRow[];
  columns: ColumnDefinition[];
  selectedRows: string[];
  onRowSelect: (rowId: string) => void;
  allowRowExpansion: boolean;
  expandedRows: string[];
  onRowExpand: (rowId: string) => void;
  renderExpandedRow?: (row: DataRow) => React.ReactNode;
  onRowAction?: (action: string, rowId: string) => void;
  showRowMenu?: boolean;
}

export const StackedRows: React.FC<StackedRowsProps> = ({
  rows,
  columns,
  selectedRows,
  onRowSelect,
  allowRowExpansion,
  expandedRows,
  onRowExpand,
  renderExpandedRow,
  onRowAction,
  showRowMenu = true,
}) => {
  return (
    <div className="divide-y divide-gray-200">
      {rows.map((row) => (
        <div key={row.id} className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox checked={selectedRows.includes(row.id)} onCheckedChange={() => onRowSelect(row.id)} />
              <div className="text-sm font-medium text-gray-900">{String(row.id)}</div>
            </div>
            {(allowRowExpansion || showRowMenu) && (
              <div className="flex items-center gap-1">
                {allowRowExpansion && (
                  <Button variant="ghost" size="icon" onClick={() => onRowExpand(row.id)}>
                    <ChevronDown className={cn('h-4 w-4 transition-transform', expandedRows.includes(row.id) && 'rotate-180')} />
                  </Button>
                )}
                {showRowMenu && (
                  <Button variant="ghost" size="icon" onClick={() => onRowAction && onRowAction('menu', row.id)}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3">
            {columns.map((col) => (
              <div key={col.key} className="">
                <div className="text-xs text-gray-500">{col.label}</div>
                <div className="text-sm text-gray-900">{renderCell(row, col)}</div>
              </div>
            ))}
          </div>

          {allowRowExpansion && expandedRows.includes(row.id) && (
            <div className="mt-3 rounded-md bg-gray-50 p-3">
              {renderExpandedRow ? renderExpandedRow(row) : <p className="text-sm text-gray-600">Expanded content for {row.id}</p>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
